import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import Button from '~components/Button/Button';
import FormDisclaimer from '~components/FormDisclaimer/FormDisclaimer';
import EmailLeadForm from '~components/LeadForm/EmailLeadForm/EmailLeadForm';
import { fireTrackEvent } from '~globals/tracking';
import useFeatureFlags from '~hooks/featureFlags/useFeatureFlags';
import useOutsideClick from '~hooks/useOutsideClick';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import checkmark from '~images/common/checkmark-decorative-circled.svg';
import CloseIcon from '~images/inline/close-circle.inline.svg';
import { toggleStickyEmailGuide } from '~redux/Actions/DisplayActions';
import { permanentlyDismissStickyCta } from '~redux/Actions/GlobalActions';

type Props = {
    className?: string;
    ctacopy?: string;
    emailsubtext?: string;
    title?: string;
};

const StickyMedicareGuideLeadForm = ({
    className = '',
    ctacopy = 'Compare plans',
    emailsubtext = 'Email',
    title = 'Download your FREE medicare guide today',
}: Props) => {
    const containerRef = useRef(null as HTMLDivElement | null);
    const formRef = useRef(null as HTMLDivElement | null);
    const btnRef = useRef(null as HTMLButtonElement | null);

    const [isSucceeded, setIsSucceeded] = useState(false);
    const [hideUntilRender, setHideUntilRender] = useState('hidden');

    const dispatch = useAppDispatch();
    const isExpanded = useAppSelector((state) => state.display.stickyGuideOpen);
    const hidePermanently = useAppSelector(
        (state) => state.global.hideDismissableStickyPermanently
    );

    const isHiddenByFeatureFlag = useFeatureFlags().HideMedicareGuideSticky();

    useOutsideClick(containerRef as React.MutableRefObject<HTMLDivElement>, () => {
        handleDismiss();
    });

    useEffect(() => {
        if (!hidePermanently) {
            setHideUntilRender('hidden md:block'); // hide from static html & when JS disabled
        }
    }, []);

    useEffect(() => {
        if (!hidePermanently) {
            dispatch(toggleStickyEmailGuide(true));
        }
    }, [dispatch]);

    useLayoutEffect(() => {
        // Used this to run the initial animation
        if (formRef.current && isExpanded) {
            formRef.current?.classList.add('translate-y-full');
            setTimeout(() => {
                formRef.current?.classList.remove('translate-y-full');
            }, 300);
        }
        // Trigger tracking on first render
        fireTrackEvent({
            page: { action: `sticky: ${isExpanded ? 'expand' : 'minimize'}` },
        });
    }, [formRef]);

    useEffect(() => {
        if (isExpanded && formRef.current) {
            formRef.current.querySelector('input')?.focus();
        }
        if (!isExpanded && btnRef.current) {
            btnRef.current.focus();
        }
    }, [isExpanded]);

    const handleDismiss = () => {
        dispatch(permanentlyDismissStickyCta());
        dispatch(toggleStickyEmailGuide(false));
    };

    const handleSuccess = () => {
        setIsSucceeded(true);
    };

    return isHiddenByFeatureFlag ? null : (
        <div
            ref={containerRef}
            className={hideUntilRender}
            aria-label={!isSucceeded ? title : 'Success!'}
        >
            <div
                ref={formRef}
                className={classNames(
                    'w-72 lg:w-80',
                    'fixed right-0 bottom-0 z-10 bg-white',
                    'transition-transform duration-700',
                    'border border-primary overflow-hidden',
                    isExpanded ? 'delay-300' : 'translate-y-full',
                    className
                )}
                data-qa="sticky-email-guide"
                aria-hidden={!isExpanded}
            >
                <div
                    className={classNames(
                        'font-poppins font-bold px-3 lg:px-6 py-3',
                        !isSucceeded
                            ? 'text-white bg-surface-primary text-20 leading-7 pr-12 lg:pr-12'
                            : 'text-black bg-white text-24 leading-8 text-center'
                    )}
                >
                    {!isSucceeded ? title : 'Success!'}
                </div>
                {!isSucceeded ? (
                    <div className="px-3 py-4 lg:px-6 lg:py-3">
                        <EmailLeadForm
                            buttonStyletype="rebrandAction"
                            buttonText={ctacopy}
                            className="w-full"
                            disabled={!isExpanded}
                            emailLabelText={emailsubtext}
                            emailLabelTextColor="black"
                            errorTextColor="text-red"
                            formPlaceholder="Enter your email address"
                            isMobileSideBySide={false}
                            isStacked={true}
                            leadInfo={{ advertiseSource: 'EnroSticky' }}
                            leadTrackingAction={{ page: { action: 'sticky: submit email' } }}
                            mktoFormId={1023}
                            onSuccess={handleSuccess}
                            qaInput="sticky-email-textbox"
                            uid="sticky-email-cta"
                        />
                        <FormDisclaimer
                            customDisclaimer="You consent to receive marketing emails from eHealth. Your information and use of this site is governed by our most recent "
                            className="text-14 lg:text-12 leading-20 lg:leading-16 mt-2"
                        />
                    </div>
                ) : (
                    <div className="pb-6 px-4">
                        <img alt="" src={checkmark} className="mx-auto mt-10 lg:mt-6" />
                        <p className="text-18 leading-7 font-open-sans w-full text-center font-semibold py-4">
                            Your free guide is on its way
                        </p>
                        <div className="flex justify-center">
                            <Button
                                styletype="reskin"
                                className="w-full"
                                onClick={() => handleDismiss()}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                )}
                <button
                    className="close-btn absolute right-1 top-2 p-2 lg:p-3"
                    onClick={() => handleDismiss()}
                    data-qa="close-sticky-email"
                    aria-label="close"
                    disabled={!isExpanded}
                >
                    <CloseIcon className="text-32" />
                </button>
            </div>
        </div>
    );
};

export default StickyMedicareGuideLeadForm;
